/**
 * kDocChartSvgMath - the LEAF of the kDocChartSvg family: the plot-frame and label data classes, the renderer's
 * constants and its pure static helpers (numbers, escaping, ticks, the value scale, label placement, pie geometry,
 * category heights, font list and theme colours). None of them reads a renderer instance.
 * Split out (2026-10-22, owner: no runtime import cycles) so kDocChartSvgAxes / Draw / Text call these helpers
 * without importing kDocChartSvgCore back; kDocChartSvgCore re-exports kDocChartFrame and kDocChartLabel and keeps
 * every static as a same-signature member, so the public surface is unchanged. The bodies are moved VERBATIM -
 * gates, try/catch and the handler labels ("kDocChartSvg.<Name>") are unchanged, so error reports are too.
 * This module imports nothing of the family at runtime (types only).
 */

import { getCoreUtils } from "./kCoreHandler";
import type { kDocTheme } from "./kDocThemeCore";
import type { kDocChart } from "./kDocChartSvgCore";

/** The plot rectangle and the value scale of a cartesian chart. */
export class kDocChartFrame {
    public X: number = 0;
    public Y: number = 0;
    public W: number = 0;
    public H: number = 0;
    public Min: number = 0;
    public Max: number = 1;
    public Ticks: number[] = [];
}

/** One value label placed over a group of bars (kDocChartWeb.LabelsAboveGroup): baseline centre, half width, text. */
export class kDocChartLabel {
    public Index: number = 0;
    public X: number = 0;
    public Y: number = 0;
    public Half: number = 0;
    public Text: string = "";
}

export class kDocChartSvgMath {
    /** Labels stacked over a group sit this many label sizes apart (a browser's text box is about 1.2-1.3 tall). */
    public static LabelLine: number = 1.45;

    // Colours when the style has none (kDocChartSvg.DefaultColors is this same array).
    public static DefaultColors: string[] = ["#414f52", "#859aa4", "#b2a78e", "#61654d", "#c7d4d5", "#000000"];

    /** A number for an SVG attribute (at most 2 decimals, "." as the separator). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Num - its gate, try/catch and handler label "kDocChartSvg.Num" stay verbatim, so error reports are unchanged.
    public static Num = (Value: number): string => {
        if (getCoreUtils().KErrorMode) { return "0"; }
        try {
            return String(Math.round((Number(Value) || 0) * 100) / 100);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Num");
            return "0";
        }
    };

    /** XML text escaping. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Escape - its gate, try/catch and handler label "kDocChartSvg.Escape" stay verbatim, so error reports are unchanged.
    public static Escape = (Text: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return String(Text).split("&").join("&amp;").split("<").join("&lt;").split(">").join("&gt;").split("\"").join("&quot;");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Escape");
            return "";
        }
    };

    /**
     * "Nice" axis ticks (steps of 1, 2 or 5 x 10^n, about Count of them) covering Min..Max. `MinStep` (default 0 =
     * none): the smallest step allowed - 1 keeps a count's axis on whole numbers.
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Ticks - its gate, try/catch and handler label "kDocChartSvg.Ticks" stay verbatim, so error reports are unchanged.
    public static Ticks = (Min: number, Max: number, Count: number, MinStep: number = 0): number[] => {
        if (getCoreUtils().KErrorMode) { return [Min, Max]; }
        try {
            const Raw: number = Math.max((Max - Min) / Math.max(1, Count), MinStep);
            const Power: number = Math.pow(10, Math.floor(Math.log10(Raw)));
            // the nearest of 1, 2, 5, 10 x 10^n (thresholds 1.5, 3, 7: the usual "nice numbers")
            const Ratio: number = Raw / Power;
            let Step: number = Power * 10;
            if (Ratio < 7) { Step = Power * 5; }
            if (Ratio < 3) { Step = Power * 2; }
            if (Ratio < 1.5) { Step = Power; }
            const Out: number[] = [];
            const First: number = Math.floor(Min / Step + 1e-9) * Step;
            for (let Value: number = First; Value <= Max + Step * 0.999; Value = Value + Step) { Out.push(Math.round(Value / Step) * Step); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Ticks");
            return [Min, Max];
        }
    };

    /** The y of a value in the plot area. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.YOf - its gate, try/catch and handler label "kDocChartSvg.YOf" stay verbatim, so error reports are unchanged.
    public static YOf = (Frame: kDocChartFrame, Value: number): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Frame.Y + Frame.H - (Value - Frame.Min) / (Frame.Max - Frame.Min) * Frame.H;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.YOf");
            return 0;
        }
    };

    /** A sort order: larger numbers first. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Descending - its gate, try/catch and handler label "kDocChartSvg.Descending" stay verbatim, so error reports are unchanged.
    public static Descending = (A: number, B: number): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return B - A;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Descending");
            return 0;
        }
    };

    /** The highest bar top (smallest y) among Boxes that reach into Left..Right; Floor when none does. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.TopIn - its gate, try/catch and handler label "kDocChartSvg.TopIn" stay verbatim, so error reports are unchanged.
    public static TopIn = (Boxes: number[][], Left: number, Right: number, Floor: number): number => {
        if (getCoreUtils().KErrorMode) { return Floor; }
        try {
            let Out: number = Floor;
            for (const Box of Boxes) {
                if (Box[1] > Left && Box[0] < Right) { Out = Math.min(Out, Box[2]); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.TopIn");
            return Floor;
        }
    };

    /** One's baseline raised by Line until it is clear of every label in Placed that it spans horizontally. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Clear - its gate, try/catch and handler label "kDocChartSvg.Clear" stay verbatim, so error reports are unchanged.
    public static Clear = (Placed: kDocChartLabel[], One: kDocChartLabel, Line: number): number => {
        if (getCoreUtils().KErrorMode) { return One.Y; }
        try {
            let Y: number = One.Y;
            let Moved: boolean = true;
            for (let Round: number = 0; Round <= Placed.length && Moved === true; Round++) {
                Moved = false;
                for (const P of Placed) {
                    const Across: boolean = P.X + P.Half > One.X - One.Half && P.X - P.Half < One.X + One.Half;
                    if (Across === true && Math.abs(P.Y - Y) < Line) { Y = P.Y - Line; Moved = true; }
                }
            }
            return Y;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Clear");
            return One.Y;
        }
    };

    /** An index that was found (not -1). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Found - its gate, try/catch and handler label "kDocChartSvg.Found" stay verbatim, so error reports are unchanged.
    public static Found = (Index: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return Index >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Found");
            return false;
        }
    };

    /** Category height without constructing a renderer. LegacyZeroFloor preserves a zero-based
     * summary's max and its stacked NaN propagation; default renderer behavior skips NaN. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.HeightOf - its gate, try/catch and handler label "kDocChartSvg.HeightOf" stay verbatim, so error reports are unchanged.
    public static HeightOf = (Chart: kDocChart, Index: number, LegacyZeroFloor: boolean = false,
                              FaultName: string = "kDocChartSvg.HeightOf"): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            let Out: number = 0;
            let Seen: boolean = false;
            for (const Series of Chart.Series) {
                const Value: number | null = Series.Values[Index];
                if (Value === null || Value === undefined || (LegacyZeroFloor === false && isNaN(Value))) { continue; }
                if (Chart.Kind === "stacked") { Out = Out + Value; continue; }
                if ((Seen === false && LegacyZeroFloor === false) || Value > Out) { Out = Value; }
                Seen = true;
            }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: central reporting retains the calling adapter's original diagnostic label.
            getCoreUtils().KGlobalErrorHandler(e, FaultName);
            return 0;
        }
    };

    /** An annulus / pie sector path from angle A0 to A1 (radians, 0 = 12 o'clock, clockwise). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Slice - its gate, try/catch and handler label "kDocChartSvg.Slice" stay verbatim, so error reports are unchanged.
    public static Slice = (Cx: number, Cy: number, R: number, Hole: number, A0: number, A1: number, Color: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            // a full circle cannot be one arc: stop just short of it
            const End: number = Math.min(A1, A0 + 2 * Math.PI - 1e-4);
            let Large: string = "0";
            if (End - A0 > Math.PI) { Large = "1"; }
            const Outer0: string = kDocChartSvgMath.Point(Cx, Cy, R, A0);
            const Outer1: string = kDocChartSvgMath.Point(Cx, Cy, R, End);
            let D: string = "M " + Outer0 + " A " + kDocChartSvgMath.Num(R) + " " + kDocChartSvgMath.Num(R) + " 0 " + Large + " 1 " + Outer1;
            if (Hole > 0) {
                D = D + " L " + kDocChartSvgMath.Point(Cx, Cy, Hole, End) + " A " + kDocChartSvgMath.Num(Hole) + " " + kDocChartSvgMath.Num(Hole) + " 0 " + Large + " 0 "
                    + kDocChartSvgMath.Point(Cx, Cy, Hole, A0) + " Z";
            }
            else { D = D + " L " + kDocChartSvgMath.Num(Cx) + " " + kDocChartSvgMath.Num(Cy) + " Z"; }
            return "<path d=\"" + D + "\" fill=\"" + Color + "\"/>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Slice");
            return "";
        }
    };

    /** "x y" of the point at Radius and angle A (0 = 12 o'clock, clockwise) around (Cx, Cy). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Point - its gate, try/catch and handler label "kDocChartSvg.Point" stay verbatim, so error reports are unchanged.
    public static Point = (Cx: number, Cy: number, Radius: number, A: number): string => {
        if (getCoreUtils().KErrorMode) { return "0 0"; }
        try {
            return kDocChartSvgMath.Num(Cx + Radius * Math.sin(A)) + " " + kDocChartSvgMath.Num(Cy - Radius * Math.cos(A));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Point");
            return "0 0";
        }
    };

    /** A font family as a CSS list ending in a generic family (see kDocChartSvgText.FamilyList). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.FamilyList - its gate, try/catch and handler label "kDocChartSvg.FamilyList" stay verbatim, so error reports are unchanged.
    public static FamilyList = (Family: string): string => {
        if (getCoreUtils().KErrorMode) { return Family; }
        try {
            if (Family.indexOf(",") >= 0 || Family.trim() === "") { return Family; }
            if (/serif/i.test(Family) === true && /sans/i.test(Family) === false) { return Family + ", serif"; }
            return Family + ", sans-serif";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.FamilyList");
            return Family;
        }
    };

    /** The series colours when the chart style has none: the theme's swatches, else DefaultColors. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.ThemeColors - its gate, try/catch and handler label "kDocChartSvg.ThemeColors" stay verbatim, so error reports are unchanged.
    public static ThemeColors = (Theme: kDocTheme): string[] => {
        if (getCoreUtils().KErrorMode) { return kDocChartSvgMath.DefaultColors; }
        try {
            const Out: string[] = [];
            const Swatches: unknown = (Theme as unknown as { Swatches?: unknown }).Swatches;
            if (Array.isArray(Swatches) === true) {
                for (const Swatch of Swatches as { Color?: unknown }[]) {
                    const Color: string = String(Swatch.Color || "").trim();
                    if (/^#[0-9a-fA-F]{6}$/.test(Color) === true && Out.indexOf(Color.toLowerCase()) < 0) { Out.push(Color.toLowerCase()); }
                }
            }
            if (Out.length === 0) { return kDocChartSvgMath.DefaultColors; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.ThemeColors");
            return kDocChartSvgMath.DefaultColors;
        }
    };
}
